import React, { useState, useEffect, useRef } from 'react';
import { api } from '../../api/client.ts';

interface TamQuocGameProps {
  onBackToHub: () => void;
}

type TroopType = 'infantry' | 'cavalry' | 'archer' | 'strategist';

interface Hero {
  id: string;
  name: string;
  type: TroopType;
  typeName: string;
  maxHp: number;
  atk: number;
  def: number;
  spd: number;
  icon: string;
  skillName: string;
}

interface CombatUnit extends Hero {
  team: 'player' | 'enemy';
  slot: number; // 0..8 (3x3 grid)
  currentHp: number;
  rage: number; // 0..100
  isDead: boolean;
}

interface CombatLog {
  attacker: string;
  target: string;
  damage: number;
  isSkill: boolean;
  isCrit: boolean;
}

const HERO_ROSTER: Hero[] = [
  {
    id: 'quan_vu',
    name: 'Quan Vũ',
    type: 'cavalry',
    typeName: 'Kỵ Binh',
    maxHp: 880,
    atk: 165,
    def: 65,
    spd: 88,
    icon: '🐉',
    skillName: 'Thanh Long Yển Nguyệt Đao',
  },
  {
    id: 'truong_phi',
    name: 'Trương Phi',
    type: 'infantry',
    typeName: 'Bộ Binh',
    maxHp: 1250,
    atk: 135,
    def: 95,
    spd: 70,
    icon: '🛡️',
    skillName: 'Tiếng Thét Cầu Trường Bản',
  },
  {
    id: 'trieu_van',
    name: 'Triệu Vân',
    type: 'cavalry',
    typeName: 'Kỵ Binh',
    maxHp: 920,
    atk: 175,
    def: 70,
    spd: 95,
    icon: '🐎',
    skillName: 'Thất Tiến Thất Xuất',
  },
  {
    id: 'hoang_trung',
    name: 'Hoàng Trung',
    type: 'archer',
    typeName: 'Cung Thủ',
    maxHp: 720,
    atk: 195,
    def: 45,
    spd: 82,
    icon: '🏹',
    skillName: 'Bách Bộ Xuyên Dương',
  },
  {
    id: 'gia_cat_luong',
    name: 'Gia Cát Lượng',
    type: 'strategist',
    typeName: 'Quân Sư',
    maxHp: 780,
    atk: 150,
    def: 55,
    spd: 92,
    icon: '🪶',
    skillName: 'Đông Phong Bát Quái Trận',
  },
  {
    id: 'ma_sieu',
    name: 'Mã Siêu',
    type: 'cavalry',
    typeName: 'Kỵ Binh',
    maxHp: 890,
    atk: 170,
    def: 62,
    spd: 90,
    icon: '⚡',
    skillName: 'Tây Lương Thiết Kỵ',
  },
];

// Stages definition
interface CampaignStage {
  id: string;
  name: string;
  enemyFormation: (Hero | null)[];
}

const CAMPAIGN_STAGES: CampaignStage[] = [
  {
    id: 'stage_1',
    name: 'Ải 1: Tiên Phong Hoàng Cân',
    enemyFormation: [
      { id: 'hc_1', name: 'Tiểu Tướng', type: 'infantry', typeName: 'Bộ Binh', maxHp: 500, atk: 75, def: 35, spd: 65, icon: '🥷', skillName: 'Cuồng Trảm' },
      null,
      { id: 'hc_2', name: 'Tiểu Tướng', type: 'infantry', typeName: 'Bộ Binh', maxHp: 500, atk: 75, def: 35, spd: 65, icon: '🥷', skillName: 'Cuồng Trảm' },
      null,
      { id: 'hc_cung', name: 'Thần Xạ Thủ', type: 'archer', typeName: 'Cung Thủ', maxHp: 420, atk: 95, def: 25, spd: 75, icon: '🏹', skillName: 'Tên Lửa' },
      null,
      null, null, null,
    ],
  },
  {
    id: 'stage_2',
    name: 'Ải 2: Trận Đột Kích Hổ Lao Quan',
    enemyFormation: [
      { id: 'hl_1', name: 'Thiết Giáp Binh', type: 'infantry', typeName: 'Bộ Binh', maxHp: 750, atk: 110, def: 60, spd: 68, icon: '🛡️', skillName: 'Thuẫn Kích' },
      { id: 'hl_2', name: 'Kỵ Binh Đổng Quân', type: 'cavalry', typeName: 'Kỵ Binh', maxHp: 650, atk: 130, def: 50, spd: 84, icon: '🐎', skillName: 'Đột Kích' },
      null,
      null,
      { id: 'hl_boss', name: 'Hoa Hùng', type: 'cavalry', typeName: 'Tướng Quân', maxHp: 1100, atk: 160, def: 70, spd: 86, icon: '⚔️', skillName: 'Đao Phách Sơn Hà' },
      null,
      null, null, null,
    ],
  },
  {
    id: 'stage_3',
    name: 'Ải 3: Chiến Thần Lữ Bố Nghênh Chiến',
    enemyFormation: [
      { id: 'lb_1', name: 'Trương Liêu', type: 'cavalry', typeName: 'Kỵ Binh', maxHp: 950, atk: 160, def: 70, spd: 88, icon: '🐎', skillName: 'Đột Kích Dũng Liệt' },
      { id: 'lb_boss', name: 'Lữ Bố Vô Song', type: 'cavalry', typeName: 'Chiến Thần', maxHp: 1650, atk: 220, def: 85, spd: 98, icon: '👹', skillName: 'Phương Thiên Họa Kích' },
      { id: 'lb_2', name: 'Cao Thuận', type: 'infantry', typeName: 'Bộ Binh', maxHp: 1100, atk: 140, def: 90, spd: 72, icon: '🛡️', skillName: 'Hãm Trận Doanh' },
      null,
      { id: 'lb_dieu', name: 'Điêu Thuyền', type: 'strategist', typeName: 'Quân Sư', maxHp: 650, atk: 120, def: 40, spd: 94, icon: '💃', skillName: 'Bế Nguyệt Khuynh Thành' },
      null,
      null, null, null,
    ],
  },
];

export const TamQuocGame: React.FC<TamQuocGameProps> = ({ onBackToHub }) => {
  // Player 9-Grid Formation: 0..8
  const [playerGrid, setPlayerGrid] = useState<(Hero | null)[]>([
    HERO_ROSTER[1], null, HERO_ROSTER[0], // Frontline (slots 0, 1, 2)
    null, HERO_ROSTER[2], null,            // Midline (slots 3, 4, 5)
    HERO_ROSTER[3], null, HERO_ROSTER[4], // Backline (slots 6, 7, 8)
  ]);

  const [selectedRosterHero, setSelectedRosterHero] = useState<Hero | null>(null);
  const [currentStageIdx, setCurrentStageIdx] = useState<number>(0);
  const stage = CAMPAIGN_STAGES[currentStageIdx];

  // Combat Animation State
  const [isBattling, setIsBattling] = useState<boolean>(false);
  const [battleRound, setBattleRound] = useState<number>(0);
  const [battleUnits, setBattleUnits] = useState<{ player: CombatUnit[]; enemy: CombatUnit[] }>({ player: [], enemy: [] });
  const [recentLogs, setRecentLogs] = useState<CombatLog[]>([]);
  const [battleResult, setBattleResult] = useState<'victory' | 'defeat' | null>(null);

  // Place selected hero on grid
  const handleGridSlotClick = (slotIdx: number) => {
    if (isBattling) return;
    if (selectedRosterHero) {
      setPlayerGrid((prev) => {
        const next = [...prev];
        // Remove hero if already placed elsewhere
        const existingIdx = next.findIndex((h) => h?.id === selectedRosterHero.id);
        if (existingIdx !== -1) next[existingIdx] = null;
        next[slotIdx] = selectedRosterHero;
        return next;
      });
      setSelectedRosterHero(null);
    } else {
      // Pick up or remove hero from slot
      const existing = playerGrid[slotIdx];
      if (existing) {
        setPlayerGrid((prev) => {
          const next = [...prev];
          next[slotIdx] = null;
          return next;
        });
      }
    }
  };

  // Start Real Turn-Based Combat Simulation
  const handleStartBattle = () => {
    // Collect active player units
    const pUnits: CombatUnit[] = [];
    playerGrid.forEach((h, slot) => {
      if (h) {
        pUnits.push({
          ...h,
          team: 'player',
          slot,
          currentHp: h.maxHp,
          rage: 0,
          isDead: false,
        });
      }
    });

    if (pUnits.length === 0) {
      alert('Vui lòng chọn ít nhất 1 tướng vào trận hình trước khi xuất quân!');
      return;
    }

    // Collect enemy units
    const eUnits: CombatUnit[] = [];
    stage.enemyFormation.forEach((h, slot) => {
      if (h) {
        eUnits.push({
          ...h,
          team: 'enemy',
          slot,
          currentHp: h.maxHp,
          rage: 0,
          isDead: false,
        });
      }
    });

    setBattleUnits({ player: pUnits, enemy: eUnits });
    setIsBattling(true);
    setBattleRound(1);
    setRecentLogs([]);
    setBattleResult(null);
  };

  // Turn-based Battle Step Engine Effect
  useEffect(() => {
    if (!isBattling || battleResult !== null) return;

    const timer = setTimeout(() => {
      setBattleUnits((prev) => {
        const pList = [...prev.player];
        const eList = [...prev.enemy];

        // Check victory / defeat
        const alivePlayers = pList.filter((u) => !u.isDead);
        const aliveEnemies = eList.filter((u) => !u.isDead);

        if (aliveEnemies.length === 0) {
          setBattleResult('victory');
          api.saveGameProgress('tam-quoc', { stage: stage.id, result: 'victory' }, `Thắng ${stage.name}`).catch(() => {});
          return prev;
        }

        if (alivePlayers.length === 0) {
          setBattleResult('defeat');
          return prev;
        }

        // All alive units sorted by SPD
        const allAlive = [...alivePlayers, ...aliveEnemies].sort((a, b) => b.spd - a.spd);
        const logs: CombatLog[] = [];

        // Execute actions for this round
        for (const actor of allAlive) {
          if (actor.isDead) continue;

          const targets = actor.team === 'player' ? eList.filter((u) => !u.isDead) : pList.filter((u) => !u.isDead);
          if (targets.length === 0) break;

          // Target selection: closest enemy in frontline or random
          const target = targets[Math.floor(Math.random() * targets.length)];

          // Damage calculation with type effectiveness
          let multiplier = 1.0;
          if (actor.type === 'cavalry' && target.type === 'archer') multiplier = 1.25;
          else if (actor.type === 'archer' && target.type === 'infantry') multiplier = 1.25;
          else if (actor.type === 'infantry' && target.type === 'cavalry') multiplier = 1.25;

          const isSkill = actor.rage >= 100;
          const isCrit = Math.random() < 0.2;
          let dmg = Math.max(20, Math.floor((actor.atk * 1.5 - target.def * 0.6) * multiplier));
          if (isSkill) dmg = Math.floor(dmg * 2.2);
          if (isCrit) dmg = Math.floor(dmg * 1.5);

          // Apply Damage
          target.currentHp = Math.max(0, target.currentHp - dmg);
          if (target.currentHp <= 0) target.isDead = true;

          // Rage update
          if (isSkill) {
            actor.rage = 0;
          } else {
            actor.rage = Math.min(100, actor.rage + 35);
          }
          target.rage = Math.min(100, target.rage + 25);

          logs.push({
            attacker: `${actor.team === 'player' ? 'Ta' : 'Địch'}: ${actor.name}`,
            target: `${target.team === 'player' ? 'Ta' : 'Địch'}: ${target.name}`,
            damage: dmg,
            isSkill,
            isCrit,
          });
        }

        setRecentLogs((l) => [...logs, ...l].slice(0, 5));
        setBattleRound((r) => r + 1);
        return { player: pList, enemy: eList };
      });
    }, 700);

    return () => clearTimeout(timer);
  }, [isBattling, battleRound, battleResult, stage]);

  return (
    <div className="max-w-md mx-auto p-3 space-y-3 select-none" style={{ paddingTop: 'calc(10px + env(safe-area-inset-top, 0px))' }}>
      {/* Top Header */}
      <div className="flex items-center justify-between pb-2 border-b border-slate-800">
        <button
          onClick={onBackToHub}
          className="px-3.5 py-2 text-sm font-bold bg-slate-800/90 hover:bg-slate-700 text-slate-200 rounded-lg flex items-center space-x-1.5 active:scale-95 transition-transform"
        >
          <span className="text-base">←</span>
          <span>GameHub</span>
        </button>

        <div className="flex items-center space-x-2">
          <span className="text-base font-black text-amber-400 tracking-wide flex items-center space-x-1.5">
            <span>🏯</span>
            <span>CHIẾN THẦN TAM QUỐC</span>
          </span>
          <span className="text-xs px-2.5 py-0.5 rounded-full bg-amber-500/20 text-amber-300 font-bold border border-amber-500/30">
            9-GRID TACTICS
          </span>
        </div>
      </div>

      {/* Stage Selector */}
      <div className="bg-slate-900/90 p-2 rounded-xl border border-slate-800 flex items-center justify-between text-xs">
        <span className="text-slate-400 font-semibold">Chiến Dịch:</span>
        <div className="flex items-center space-x-1">
          {CAMPAIGN_STAGES.map((s, idx) => (
            <button
              key={`stage_${s.id}`}
              disabled={isBattling}
              onClick={() => {
                setCurrentStageIdx(idx);
                setIsBattling(false);
                setBattleResult(null);
              }}
              className={`px-2.5 py-1 rounded-lg font-bold transition-all ${
                currentStageIdx === idx
                  ? 'bg-amber-500 text-slate-950 shadow'
                  : 'bg-slate-800 text-slate-400 hover:text-slate-200'
              }`}
            >
              Ải {idx + 1}
            </button>
          ))}
        </div>
      </div>

      {/* BATTLE SIMULATOR VIEW (IF BATTLING) */}
      {isBattling ? (
        <div className="space-y-3">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-3.5 space-y-3 shadow-xl">
            <div className="flex items-center justify-between text-xs font-bold">
              <span className="text-emerald-400">ĐỘI QUÂN TA</span>
              <span className="px-2 py-0.5 bg-slate-800 rounded text-slate-300">HIỆP {battleRound}</span>
              <span className="text-rose-400">QUÂN ĐỊCH</span>
            </div>

            {/* Combat Visual: 2 Formations facing each other */}
            <div className="grid grid-cols-2 gap-3 py-1">
              {/* Player Formation */}
              <div className="grid grid-cols-3 gap-1.5 bg-slate-950/70 p-2 rounded-xl border border-emerald-500/30">
                {Array.from({ length: 9 }).map((_, slot) => {
                  const unit = battleUnits.player.find((u) => u.slot === slot);
                  return (
                    <div
                      key={`p_combat_${slot}`}
                      className={`h-16 rounded-lg p-1 flex flex-col items-center justify-center border transition-all ${
                        unit
                          ? unit.isDead
                            ? 'bg-slate-900/40 border-slate-800 opacity-40'
                            : 'bg-emerald-950/40 border-emerald-500/60 shadow'
                          : 'border-slate-800/40 bg-transparent'
                      }`}
                    >
                      {unit && (
                        <>
                          <span className="text-lg">{unit.icon}</span>
                          <span className="text-[10px] font-bold text-slate-200 truncate w-full text-center">
                            {unit.name}
                          </span>
                          {!unit.isDead && (
                            <div className="w-full h-1 bg-slate-800 rounded-full mt-0.5 overflow-hidden">
                              <div
                                className="h-full bg-emerald-400"
                                style={{ width: `${(unit.currentHp / unit.maxHp) * 100}%` }}
                              />
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Enemy Formation */}
              <div className="grid grid-cols-3 gap-1.5 bg-slate-950/70 p-2 rounded-xl border border-rose-500/30">
                {Array.from({ length: 9 }).map((_, slot) => {
                  const unit = battleUnits.enemy.find((u) => u.slot === slot);
                  return (
                    <div
                      key={`e_combat_${slot}`}
                      className={`h-16 rounded-lg p-1 flex flex-col items-center justify-center border transition-all ${
                        unit
                          ? unit.isDead
                            ? 'bg-slate-900/40 border-slate-800 opacity-40'
                            : 'bg-rose-950/40 border-rose-500/60 shadow'
                          : 'border-slate-800/40 bg-transparent'
                      }`}
                    >
                      {unit && (
                        <>
                          <span className="text-lg">{unit.icon}</span>
                          <span className="text-[10px] font-bold text-slate-200 truncate w-full text-center">
                            {unit.name}
                          </span>
                          {!unit.isDead && (
                            <div className="w-full h-1 bg-slate-800 rounded-full mt-0.5 overflow-hidden">
                              <div
                                className="h-full bg-rose-400"
                                style={{ width: `${(unit.currentHp / unit.maxHp) * 100}%` }}
                              />
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Combat Replay Feed */}
            <div className="bg-slate-950/90 p-2.5 rounded-xl border border-slate-800 text-xs space-y-1 font-mono">
              {recentLogs.map((log, idx) => (
                <div key={`log_${idx}`} className="text-[11px] flex justify-between items-center text-slate-300">
                  <span>
                    {log.attacker} {log.isSkill ? '⚡ TUYỆT KỸ!' : 'tấn công'} {log.target}
                  </span>
                  <span className={`font-bold ${log.isCrit ? 'text-rose-400 font-black' : 'text-amber-400'}`}>
                    -{log.damage} HP
                  </span>
                </div>
              ))}
            </div>

            {/* Result Modal Banner */}
            {battleResult && (
              <div className="p-4 rounded-xl bg-gradient-to-r from-amber-500/20 via-emerald-500/20 to-blue-500/20 border border-amber-500/40 text-center space-y-2">
                <div className="text-2xl font-black">
                  {battleResult === 'victory' ? '🏆 ĐẠI THẮNG QUÂN NGHỊCH!' : '💀 THẤT THỦ TRẬN ĐỊA!'}
                </div>
                <p className="text-xs text-slate-300">
                  {battleResult === 'victory'
                    ? `Thần tướng đã phá vỡ hoàn toàn phòng tuyến của ${stage.name}!`
                    : 'Binh lực tổn hao, hãy bố trí lại thế trận 3x3 và nâng cao khắc chế binh chủng!'}
                </p>
                <button
                  onClick={() => setIsBattling(false)}
                  className="px-5 py-2 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-black text-xs shadow active:scale-95"
                >
                  Trở Về Bày Trận
                </button>
              </div>
            )}
          </div>
        </div>
      ) : (
        /* FORMATION BUILDER (9-GRID) */
        <div className="space-y-3">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-3.5 space-y-3 shadow-xl">
            <div className="flex justify-between items-center text-xs">
              <span className="font-bold text-amber-300">BÀY TRẬN BÁT QUÁI (3x3)</span>
              <span className="text-[11px] text-slate-400">Chạm vào ô để đặt/gỡ tướng</span>
            </div>

            {/* 3x3 Player Tactical Grid */}
            <div className="grid grid-cols-3 gap-2 bg-slate-950/80 p-3 rounded-xl border border-slate-800">
              {playerGrid.map((hero, idx) => (
                <button
                  key={`slot_${idx}`}
                  onClick={() => handleGridSlotClick(idx)}
                  className={`h-20 rounded-xl p-2 border flex flex-col items-center justify-center transition-all active:scale-95 ${
                    hero
                      ? 'bg-slate-800 border-amber-500/70 text-slate-100 shadow-lg ring-1 ring-amber-500/30'
                      : 'bg-slate-900/40 border-slate-800/80 border-dashed text-slate-500 hover:border-slate-700'
                  }`}
                >
                  {hero ? (
                    <>
                      <span className="text-2xl">{hero.icon}</span>
                      <span className="text-[11px] font-bold text-amber-300 truncate w-full text-center">
                        {hero.name}
                      </span>
                      <span className="text-[9px] text-slate-400">HP {hero.maxHp}</span>
                    </>
                  ) : (
                    <span className="text-[10px] text-slate-600">Ô {idx + 1} (Trống)</span>
                  )}
                </button>
              ))}
            </div>

            <div className="text-[11px] text-slate-400 text-center">
              Khắc chế: 🐎 Kỵ binh &gt; 🏹 Cung thủ &gt; 🛡️ Bộ binh &gt; 🐎 Kỵ binh (+25% sát thương)
            </div>

            <button
              onClick={handleStartBattle}
              className="w-full py-3 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-slate-950 font-black text-sm shadow-xl active:scale-95 transition-all uppercase tracking-wider"
            >
              ⚔️ Xuất Quân Nghênh Chiến ({stage.name})
            </button>
          </div>

          {/* Roster of Available Heroes */}
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-3.5 space-y-2 shadow-xl">
            <h4 className="text-xs font-bold text-slate-300">TƯỚNG LĨNH SẴN SÀNG CHIẾN ĐẤU:</h4>
            <div className="grid grid-cols-3 gap-2">
              {HERO_ROSTER.map((hero) => {
                const isPlaced = playerGrid.some((h) => h?.id === hero.id);
                const isSelected = selectedRosterHero?.id === hero.id;

                return (
                  <button
                    key={`roster_${hero.id}`}
                    onClick={() => setSelectedRosterHero(isSelected ? null : hero)}
                    className={`p-2 rounded-xl border text-left flex flex-col items-center space-y-0.5 transition-all ${
                      isSelected
                        ? 'bg-amber-500/20 border-amber-400 ring-2 ring-amber-400/50'
                        : isPlaced
                        ? 'bg-slate-800/40 border-slate-800 opacity-50'
                        : 'bg-slate-800 border-slate-700 hover:border-slate-600'
                    }`}
                  >
                    <span className="text-2xl">{hero.icon}</span>
                    <span className="text-xs font-bold text-slate-100">{hero.name}</span>
                    <span className="text-[10px] text-amber-400 font-semibold">{hero.typeName}</span>
                  </button>
                );
              })}
            </div>
            {selectedRosterHero && (
              <p className="text-[11px] text-amber-300 text-center animate-pulse pt-1">
                Đang chọn <strong>{selectedRosterHero.name}</strong>. Hãy bấm vào ô trong 9 ô ở trên để xếp vị trí!
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
